import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil, Plus } from "lucide-react"

import { DataState } from "@/components/DataState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { PERSON_NATURE_LABELS, type CostCenter } from "@/lib/types"
import { onWriteError } from "@/lib/writeErrors"

import { FieldsDialog, type FieldSpec, type FieldValue } from "@/routes/masters/FieldsDialog"
import { fetchCostCenters, saveCostCenter } from "@/routes/masters/api"
import { t } from "@/lib/i18n"

const FIELDS: FieldSpec[] = [
  { type: "text", name: "code", label: "Codigo", required: true, placeholder: "CC-OPE" },
  { type: "text", name: "name", label: "Nombre", required: true },
  { type: "text", name: "area", label: "Area", required: true },
  {
    type: "select",
    name: "defaultNature",
    label: "Naturaleza por defecto",
    required: true,
    wide: true,
    options: [
      { value: "costo_venta", label: "Costo de ventas" },
      { value: "gasto_operativo", label: "Gasto operativo" },
      { value: "capex", label: "Capex" },
    ],
    help: "Es solo el valor sugerido al dar de alta una persona: en el alta se decide caso por caso.",
  },
]

export function CostCentersPage() {
  const { can } = useRbac()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState<CostCenter | null>(null)
  const [creating, setCreating] = React.useState(false)

  const query = useQuery({ queryKey: ["cost-centers"], queryFn: fetchCostCenters })

  const mutation = useMutation({
    mutationFn: (payload: { values: Record<string, FieldValue>; reason: string; id?: string }) =>
      saveCostCenter({
        data: { ...(payload.values as Partial<CostCenter>), id: payload.id },
        reason: payload.reason,
      }),
    onSuccess: () => {
      toast({ title: "Centro de costo guardado" })
      setEditing(null)
      setCreating(false)
      void queryClient.invalidateQueries({ queryKey: ["cost-centers"] })
    },
    onError: onWriteError("el centro de costo"),
  })

  const costCenters = query.data ?? []

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="max-w-[80ch] text-xs leading-[1.15rem] text-muted-foreground">
          {t("Los centros de costo agrupan el gasto y el P&L por area, y se mapean al plan gerencial.")}</p>
        <Button size="sm" disabled={!can("edit")} onClick={() => setCreating(true)}>
          <Plus />
          {t("Nuevo centro de costo")}</Button>
      </div>

      <div className="overflow-hidden rounded-panel border bg-surface shadow-xs">
        <DataState
          loading={query.isLoading}
          error={query.error}
          onRetry={() => void query.refetch()}
          empty={costCenters.length === 0}
          emptyMessage={t("Todavia no hay centros de costo.")}
          emptyHint={t("Sin centros de costo el P&L solo se puede leer por cuenta, no por area. Es la dimension que permite responder «cuanto cuesta entregar» frente a «cuanto cuesta la estructura».")}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">{t("Codigo")}</TableHead>
                <TableHead>{t("Nombre")}</TableHead>
                <TableHead className="w-40">{t("Area")}</TableHead>
                <TableHead className="w-44">{t("Naturaleza por defecto")}</TableHead>
                <TableHead className="w-24">{t("Estado")}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {costCenters.map((costCenter) => (
                <TableRow key={costCenter.id}>
                  <TableCell className="code">{costCenter.code}</TableCell>
                  <TableCell className="font-medium">{costCenter.name}</TableCell>
                  <TableCell>{costCenter.area}</TableCell>
                  <TableCell className="text-2xs">
                    {PERSON_NATURE_LABELS[costCenter.defaultNature]}
                  </TableCell>
                  <TableCell>
                    <Badge variant={costCenter.status === "activo" ? "success" : "outline"}>
                      {costCenter.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={!can("edit")}
                      onClick={() => setEditing(costCenter)}
                    >
                      <Pencil />
                      {t("Editar")}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DataState>
      </div>

      <FieldsDialog
        open={Boolean(editing) || creating}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null)
            setCreating(false)
          }
        }}
        title={editing ? `Editar centro de costo · ${editing.code}` : "Nuevo centro de costo"}
        fields={FIELDS}
        initial={
          editing
            ? {
                code: editing.code,
                name: editing.name,
                area: editing.area,
                defaultNature: editing.defaultNature,
              }
            : { defaultNature: "gasto_operativo" }
        }
        pending={mutation.isPending}
        onSubmit={(values, reason) =>
          mutation.mutateAsync({ values, reason, id: editing?.id }).then(() => undefined)
        }
      />
    </div>
  )
}
